import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { navItemForPath } from '@/lib/navigation';

/**
 * The one header every screen sits under:
 *
 *     [ collapse | ← | Section | Detail ]
 *
 * The section is derived from the route, not passed in, so a detail page cannot
 * disagree with the sidebar about where it belongs — `navItemForPath` matches
 * the longest prefix, which is what ties `/orders/abc` back to Orders.
 *
 * The detail crumb arrives through context rather than by each page rendering
 * its own header. One header in the shell means the sticky bar never doubles up
 * or shifts as you move between a list and a record.
 *
 * The back arrow comes from the same derivation, so every record and every
 * "new" form gets one in the same place without asking the page for it. It
 * goes to the section's list rather than through history: a record reached
 * from a search, a toast or a fresh tab has no useful "back", and landing on
 * the list is right in all of those cases.
 */

const DetailTitleContext = createContext<Dispatch<SetStateAction<string | null>> | null>(null);
const DetailTitleValue = createContext<string | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);

  return (
    <DetailTitleContext.Provider value={setTitle}>
      <DetailTitleValue.Provider value={title}>{children}</DetailTitleValue.Provider>
    </DetailTitleContext.Provider>
  );
}

/**
 * Publish the detail crumb from a record page.
 *
 * Clears on unmount, so navigating from a record back to its list drops the
 * crumb instead of stranding a stale one in the bar.
 */
export function useDetailTitle(title: string | null) {
  const setTitle = useContext(DetailTitleContext);

  useEffect(() => {
    setTitle?.(title);
    return () => setTitle?.(null);
  }, [title, setTitle]);
}

export function PageHeader({ actions }: { actions?: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const detail = useContext(DetailTitleValue);
  const section = navItemForPath(pathname);

  // Anything below a section's own route is a record or a form opened from it.
  const parent = section && pathname !== section.to ? section : undefined;

  return (
    /*
     * `rounded-t-xl` matches the inset panel this sits at the top of (see
     * `SidebarInset`). Without it the header's square corners paint over the
     * panel's rounded ones, and the console reads as a page with a chipped
     * top edge. Clipping the panel instead is not an option: `overflow-hidden`
     * would make it a scroll container and this bar would stop sticking.
     */
    <header className="bg-background sticky top-0 z-10 flex h-16 shrink-0 items-center border-b md:rounded-t-xl">
      <div className="flex w-full items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 data-[orientation=vertical]:h-4" />

        {parent && (
          <Button asChild variant="ghost" size="icon" className="-ml-1 size-7 shrink-0">
            <Link to={parent.to} aria-label={`Back to ${parent.title}`}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
        )}

        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              {section ? (
                // Once there is a detail crumb the section becomes the way back
                // to its list; on the list itself it is the current page.
                detail ? (
                  <BreadcrumbLink asChild>
                    <Link to={section.to}>{section.title}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{section.title}</BreadcrumbPage>
                )
              ) : (
                <BreadcrumbPage>ctrl</BreadcrumbPage>
              )}
            </BreadcrumbItem>

            {detail && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="max-w-[40ch] truncate">{detail}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>

        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
